import { LoadingOutlined } from "@ant-design/icons"
import {
	Col,
	Typography,
	Result,
	Row,
	Spin,
	Form,
} from "antd"
import Axios from "axios"
import React, { useState } from "react"
import { Config } from "./config_section.js"
import "./CareerPathVut.scss"
import AddEditSkillModalForm from "./AddEditSkillModalForm"
import { Statistics } from "./Statistics"
import { TrainingsBrowserModal } from "./TrainingsBrowser/TrainingsBrowser"
import { combineSkillsWithImportedSkills, createSkillId } from "./utils"
import { AddSkillButton } from "./AddSkillButton"
import { SkillList } from "./SkillList"
import { ImportButton } from "./ImportExportButtons/ImportButton"
import { ExportButton } from "./ImportExportButtons/ExportButton"

const { Title } = Typography

const CareerPathVut = ( { showMessage } ) => {
	const [ skills, setSkills ] = React.useState( [] )
	const [ editRecord, setEditRecord ] = React.useState( null )
	const [ rsSkillsGroup, setRsSkillsGroup ] = React.useState( null )
	const [ rsSpecificKnowledge, setRsSpecificKnowledge ] = React.useState( null )
	const [ rsSpecificSkills, setRsSpecificSkills ] = React.useState( null )
	const [ rsProfiles, setRsProfiles ] = React.useState( null )
	const [ trainings, setTrainings ] = useState( null )
	const [ trainingsBrowserCtx, setTrainingsBrowserCtx ] = useState( {
		open: false,
		missingSkillsGroup: [],
	} )
	const [ analyzing, setAnalyzing ] = useState( false )
	const [ analysisForm ] = Form.useForm()

	React.useEffect( () => {
		getData()
	}, [] )

	function getData() {
		// loading data state
		setRsSkillsGroup( null )
		setRsSpecificKnowledge( null )
		setRsSpecificSkills( null )
		setRsProfiles( null )
		setTrainings( null )
		Axios.get( Config.server.getData )
			.then( ( response ) => {
				if ( response && response.data.error === false ) {
					// success data state
					setRsSkillsGroup( response.data.data.rs_skill_group )
					setRsSpecificKnowledge( response.data.data.rs_specific_knowledge )
					setRsSpecificSkills( response.data.data.rs_specific_skills )
					setRsProfiles( response.data.data.rs_profiles )
					setTrainings( response.data.data.trainings )
				} else {
					// error data state
					setRsSkillsGroup( 0 )
					setRsSpecificKnowledge( 0 )
					setRsSpecificSkills( 0 )
					setRsProfiles( 0 )
					setTrainings( 0 )
				}
			} )
			.catch( ( error ) => {
				console.log( error )
				setRsSkillsGroup( 0 )
				setRsSpecificKnowledge( 0 )
				setRsSpecificSkills( 0 )
				setRsProfiles( 0 )
				setTrainings( 0 )
			} )
	}

	const dataStatus = React.useMemo( () => {
		if (
			rsSkillsGroup === null
			&& rsSpecificKnowledge === null
			&& rsSpecificSkills === null
			&& rsProfiles === null
			&& trainings === null
		) {
			return "loading"
		} else if (
			Array.isArray( rsSkillsGroup )
			&& Array.isArray( rsSpecificKnowledge )
			&& Array.isArray( rsSpecificSkills )
			&& Array.isArray( rsProfiles )
			&& Array.isArray( trainings )
		) {
			return "ok"
		} else {
			return "error"
		}
	}, [ rsSkillsGroup, rsSpecificKnowledge, rsSpecificSkills, rsProfiles, trainings ] )

	const onModalSubmit = ( values, isNew ) => {
		if ( isNew ) {
			if ( !values.id ) {
				values.id = createSkillId()
			}
			values.enabled = true
			setSkills( [ ...skills, values ] )
		} else {
			setSkills( skills.map( ( item ) => ( item.id === values.id ? { ...item, ...values } : item ) ) )
		}
		showMessage( { type: "success", content: "Skill saved successfully!" } )
		setEditRecord( null )
	}

	const handleSkillCheckboxChange = ( _e, skill ) => {
		setSkills( oldSkills =>
			oldSkills.map( ( oldSkill ) => {
				if ( oldSkill.id === skill.id ) {
					return { ...oldSkill, enabled: !oldSkill.enabled }
				}
				return oldSkill
			} )
		)
	}

	const handleSkillEdit = ( _e, skill ) => {
		setEditRecord( skill )
	}

	const handleSkillDelete = ( _e, skillToDelete ) => {
		setSkills( list => list.filter( skill => skill !== skillToDelete ) )
	}

	return (
		<section id={ "career-path-vut" }>
			<div className={ "section-container" }>
				<div className={ "text-content" }>
					<Title level={ 1 } className={ "pageTitle" }>
						{ "Career Path" }
					</Title>
					{ dataStatus === "loading"
						? (
							<div className={ "vertCenter" }>
								<Spin tip={ "Loading..." } indicator={ <LoadingOutlined style={ { fontSize: 24 } } spin /> } />
							</div>
						)
						: dataStatus === "error"
						? (
							<Result
								status={ "500" }
								title={ "500" }
								subTitle={ "Sorry, something went wrong." }
							/>
						)
						: (
							<>
								<Row>
									<Col span={ 12 } className={ "left-col" }>
										<div className={ "list-container-title" }>
											<Title level={ 2 } className={ "col-title" }>{ "Your Skills" }</Title>
										</div>
										<div className={ "list-container-actions" }>
											<AddSkillButton
												onAnalyzingStart={ () => setAnalyzing( true ) }
												onAnalyzingEnd={ () => setAnalyzing( false ) }
												form={ analysisForm }
												showMessage={ showMessage }
												openAddForm={ () => setEditRecord( "new" ) }
												onPickTrainings={ () => {
													setTrainingsBrowserCtx( {
														open: true,
														missingSkillsGroup: [],
													} )
												} }
												analyzing={ analyzing }
											/>
											<div>
												<ImportButton
													showMessage={ showMessage }
													onImportSuccess={ ( json ) => {
														setSkills( old => combineSkillsWithImportedSkills( json.skills, old, trainings ) )
													} }
												/>
												<ExportButton
													isExportable={ !skills.length }
													getExportData={ () => ( {
														skills: skills.map( skill =>
															skill.skillType === "training"
																? { skillType: "training", trainingId: skill.trainingId, enabled: skill.enabled }
																: skill
														),
													} ) }
													showMessage={ showMessage }
												/>
											</div>
										</div>

										<div className={ "list-container" }>
											<SkillList
												skills={ skills }
												rsSkillsGroup={ rsSkillsGroup }
												onSkillCheckboxChange={ handleSkillCheckboxChange }
												onSkillEdit={ handleSkillEdit }
												onSkillDelete={ handleSkillDelete }
											/>
										</div>
									</Col>

									<Col span={ 12 } className={ "right-col" }>
										<div className={ "statistics-title" }>
											<Title level={ 2 } className={ "col-title" }>{ "Statistics" }</Title>
										</div>

										<Statistics
											skills={ skills.filter( skill => skill.enabled ) }
											rsSkillsGroup={ rsSkillsGroup }
											rsProfiles={ rsProfiles }
											onTrainingsSearchClick={ ( _e, missingSkillsGroup ) => {
												setTrainingsBrowserCtx( {
													open: true,
													missingSkillsGroup,
												} )
											} }
										/>
									</Col>
								</Row>

								<AddEditSkillModalForm
									showMessage={ showMessage }
									editRecord={ editRecord }
									onCancel={ () => setEditRecord( null ) }
									onModalSubmit={ onModalSubmit }
									rsSkillsGroup={ rsSkillsGroup }
									form={ analysisForm }
									analyzing={ analyzing }
									setAnalyzing={ setAnalyzing }
								/>
								<TrainingsBrowserModal
									showMessage={ showMessage }
									open={ trainingsBrowserCtx.open }
									initialSkillsGroup={ trainingsBrowserCtx.missingSkillsGroup }
									rsSkillsGroup={ rsSkillsGroup }
									trainings={ trainings }
									onClose={ () =>
										setTrainingsBrowserCtx( {
											open: false,
											missingSkillsGroup: [],
										} ) }
									onTrainingsPick={ ( pickedTrainings ) => {
										setSkills(
											[
												...skills,
												...pickedTrainings.map( training => ( {
													id: createSkillId( training.id ),
													skillType: "training",
													trainingId: training.id,
													name: training.name,
													enabled: true,
													skillsGroup: training.skills_group,
													training: training,
												} ) ),
											],
										)
									} }
									skills={ skills }
								/>
							</>
						) }
				</div>
			</div>
		</section>
	)
}

export default CareerPathVut
